import { useI18n, type Lang } from "../../i18n/I18nProvider";

const LABEL_KEYS = {
  en: "lang.en",
  sr: "lang.rs",
  ru: "lang.ru",
} as const satisfies Record<Lang, string>;

const DEFAULT_LANGUAGES: readonly Lang[] = ["en", "sr"];

interface LanguageToggleProps {
  /** Languages to offer, in order. Defaults to EN and RS. */
  languages?: readonly Lang[];
}

/** "EN | RS" segmented control. Each choice is a 44px target; the current language is bold and pressed. */
export function LanguageToggle({ languages = DEFAULT_LANGUAGES }: LanguageToggleProps) {
  const { lang, setLang, t } = useI18n();
  const options = languages.map((code) => ({ lang: code, labelKey: LABEL_KEYS[code] }));
  return (
    <div
      role="group"
      aria-label={t("lang.label")}
      className="flex items-center rounded-control border border-edge bg-board text-operate"
    >
      {options.map((option, index) => {
        const current = option.lang === lang;
        return (
          <span key={option.lang} className="flex items-center">
            {index > 0 ? (
              <span className="text-edge" aria-hidden="true">
                |
              </span>
            ) : null}
            <button
              type="button"
              aria-pressed={current}
              onClick={() => setLang(option.lang)}
              className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-control px-2 transition-colors duration-[120ms] hover:text-ink ${current ? "font-bold text-ink" : "text-ink-2"}`}
            >
              {t(option.labelKey)}
            </button>
          </span>
        );
      })}
    </div>
  );
}
